import { Field, Input, Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";

import { JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { IconCaretRight, IconCheck, IconMagnifyingGlass } from "@/components/icons";

import { useLang } from "@/hooks/useLang";
import { cn } from "@/utils/cn.ts";

interface DropdownProps {
  title: string;
  isSearch?: boolean;
  values: { id: number | string; name: string }[];
  paramName: string;
}

const FilterListbox = ({ title, isSearch, values, paramName }: DropdownProps): JSX.Element => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramValue = searchParams.get(paramName);

  const [selectedValue, setSelectedValue] = useState<string>(paramValue || "");
  const [searchValue, setSearchValue] = useState("");
  const [filteredValues, setFilteredValues] = useState<typeof values>(values);
  const lang = useLang();

  const filterValues = (searchValue: string) => {
    setSearchValue(searchValue);
    setFilteredValues(values.filter((value) => value.name.toLowerCase().includes(searchValue.toLowerCase())));
  };

  const setFilterParam = (param: string): void => {
    const params = new URLSearchParams(searchParams);
    !param ? params.delete(paramName) : params.set(paramName, param);
    setSearchParams(params);
  };

  useEffect(() => {
    setSelectedValue(paramValue || "");
  }, [paramValue]);

  useEffect(() => {
    setFilteredValues(values);
  }, [lang]);

  return (
    <Field className={"flex flex-col gap-y-2.5"}>
      <Label className={" text-sm/normal font-semibold uppercase"}>{title}</Label>
      <Listbox value={selectedValue}>
        {({ open }) => (
          <>
            <ListboxButton
              className={
                "flex min-w-[13.125rem] items-center justify-between gap-3 rounded-full bg-surface-container px-5 py-3 text-base/normal font-medium text-on-surface-variant"
              }
            >
              {values.find((value) => String(value.id) === selectedValue)?.name || t("General.all")}
              <IconCaretRight
                className={cn("size-4 rotate-90 stroke-on-surface-variant transition-transform", {
                  "-rotate-90": open,
                })}
              />
            </ListboxButton>
            <ListboxOptions
              transition
              anchor={"bottom"}
              modal={false}
              className={cn(
                "mt-2 w-[var(--button-width)] origin-top !overflow-hidden rounded-[1.25rem] bg-surface-bright focus:outline-none",
                "z-[100] transition duration-100 ease-in data-[closed]:scale-75 data-[closed]:opacity-0 max-md:bg-surface-container-high",
              )}
            >
              <div className={"max-h-[27.313rem] overflow-y-auto max-lg:max-h-[21.75rem]"}>
                {isSearch ? (
                  <div className={"relative px-5 pb-2.5 pt-5"}>
                    <div className={"relative"}>
                      <Input
                        value={searchValue}
                        onChange={(e) => filterValues(e.target.value)}
                        placeholder={t("Actions.search")}
                        className={cn(
                          "w-full rounded-full bg-surface-container py-3 pl-4 pr-10 focus:outline-none",
                          "text-on-primary placeholder:text-on-surface",
                        )}
                      />
                      <IconMagnifyingGlass
                        className={"absolute right-4 top-1/2 size-6 -translate-y-1/2 transform stroke-on-surface"}
                      />
                    </div>
                  </div>
                ) : null}
                <ListboxOption
                  value={""}
                  onClick={() => setFilterParam("")}
                  className={cn(
                    "group flex cursor-pointer select-none items-center gap-2 px-5 py-4 transition data-[focus]:bg-surface-container",
                    "text-sm/6 text-white",
                    "flex items-center justify-between",
                    { "cursor-default": !selectedValue },
                  )}
                >
                  {t("General.all")}
                  {!paramValue ? <IconCheck className={"size-4 stroke-on-primary"} /> : null}
                </ListboxOption>
                {filteredValues.map((value) => (
                  <ListboxOption
                    key={value.id}
                    value={value}
                    onClick={() => setFilterParam(value.id.toString())}
                    className={cn(
                      "group flex select-none items-center gap-2 px-5 py-4 transition data-[focus]:bg-surface-container",
                      "cursor-pointer text-sm/6 text-white",
                      "flex items-center justify-between",
                      { "cursor-default": value.id.toString() === selectedValue },
                    )}
                  >
                    {value.name}
                    {value.id.toString() === selectedValue ? (
                      <IconCheck className={"size-4 stroke-on-primary"} />
                    ) : null}
                  </ListboxOption>
                ))}
              </div>
            </ListboxOptions>
          </>
        )}
      </Listbox>
    </Field>
  );
};

export default FilterListbox;
